<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
//import DateInput from '../ui/form/DateInput.vue';
import { api } from '../../api/Api';
import { formatDate, formatNumber } from '../../utils/CommonFunctions';
import Buttons from '../ui/form/Buttons.vue';
import ModalFormTemplate from '../ui/Templates/ModalFormTemplate.vue';
import Toast from '../../services/Toast';


// Filter states
const fromDate = ref<Date | string>('');
const toDate = ref<Date | string>('');
const selectedOutlet = ref(null);

// Dialog state
const showTransactions = ref(false);
const selectedTransactions = ref<any[]>([]);
const dialogTitle = ref('');

// Show transaction history
const showTransactionHistory = (account: ProfitLoss) => {
  dialogTitle.value = account.account_name;
  getTransactions(account);
};

const getTransactions = async (account: ProfitLoss) => {
  try {
    const response = await api.post("/clients/reports/transactions", {
      from_date: fromDate.value,
      to_date: toDate.value,
      chart_of_accounts_id: account.chart_of_accounts_id,
    });
    if (response.success == true) {
      selectedTransactions.value = response.data;
      showTransactions.value = true;
    }
  } catch (error) {
    Toast.show(String(error) || "Failed to fetch transactions", 'error');
  }
}

const getData = async () => {
  try {
    currentPage.value = 1;
    const response = await api.post("/clients/reports/balance-sheet", {
      from_date: fromDate.value,
      to_date: toDate.value,
      outlet: selectedOutlet.value
    });
    if (response.success == true) {
      data.value = response.data;
    }
  } catch (error) {
    Toast.show(String(error) || "Failed to fetch Data", 'error');
  }
}

interface ProfitLoss {
  account_name: string;
  amount: number;
  category: string;
  chart_of_accounts_id: number | null;
  indent: number;
  is_category: number;
  is_subtotal: number;
  parent: string | null;
  sort_order: number;
  monthly?: Record<string, number>;
}
const data = ref<ProfitLoss[]>([]);

onMounted(() => {
  // Initialize date range to the last date of the current year
  const today = new Date();
  fromDate.value = formatDate(new Date(today.getFullYear(), 0, 1));
  toDate.value = formatDate(new Date(today.getFullYear(), 11, 31));
  getData();
});

const pageSize = 25;
const currentPage = ref(1);

const totalPages = computed(() => Math.ceil(selectedTransactions.value.length / pageSize));

const paginatedTransactions = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return selectedTransactions.value.slice(start, start + pageSize);
});

function prevPage() {
  if (currentPage.value > 1) currentPage.value--;
}
function nextPage() {
  if (currentPage.value < totalPages.value) currentPage.value++;
}



</script>

<template>
  <div class="profit-loss">
    <h1 class="report-title">Balance Sheet</h1>
    
    <!-- Filters -->
    <!--<div class="filters">
      <div class="filter-group">
          <DateInput 
            v-model="fromDate" 
            :showIcon="true"
            dateFormat="yyy-mm-dd"
            label="From Date"
          />
      </div>
      
      <div class="filter-group">
        <DateInput 
            v-model="toDate" 
            :showIcon="true"
            dateFormat="yyy-mm-dd"
            label="To Date"
          />
      </div>

      <div class="filter-actions">
        <div>
        <Buttons caption="Generate" icon="RotateCw" @click="getData" />
        </div>
      </div>
    </div>-->

    <div class="report-container">
      <template v-for="d in data">
        <div class="section" v-if="d.is_category">
          <h2 class="section-header">{{d.account_name}}</h2>
        </div>
        <div class="line-item" v-if="d.chart_of_accounts_id" @dblclick="showTransactionHistory(d)">
          <span class="item-label">{{d.account_name}}</span>
          <span class="item-label">{{ formatNumber(d.amount) }}</span>
        </div>
        <div class="subtotal" v-if="d.is_subtotal && d.account_name != 'Net Profit'">
          <span>{{d.account_name}}</span>
          <span>{{ formatNumber(d.amount) }}</span>
        </div>
        <div class="major-total net-profit" v-if="d.is_subtotal && d.account_name == 'Net Profit'">
          <span>Net Profit</span>
          <span>{{ formatNumber(d.amount) }}</span>
        </div>
      </template>
    </div>
    

    <!-- Transaction History Dialog -->
    <ModalFormTemplate :mode="'view'" :title="dialogTitle + ' - Transaction History'" end-point=""
    v-if="showTransactions"  :ignore-permission="true"
    @close="showTransactions = false">
    <template #form>
      <div class="transaction-history">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th class="text-right" style="text-align: right;">Debit</th>
              <th class="text-right" style="text-align: right;">Credit</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(transaction, index) in paginatedTransactions" :key="index">
              <td>{{ formatDate(transaction.date) }}</td>
              <td>{{ transaction.record_type }}</td>
              <td class="amount">{{ formatNumber(transaction.debit_amount) }}</td>
              <td class="amount">{{ formatNumber(transaction.credit_amount) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="2" style="text-align: right; font-weight: bold;">Total:</td>
              <td class="amount" style="font-weight: bold;">{{ formatNumber(selectedTransactions.reduce((sum, t) => sum + Number(t.debit_amount), 0)) }}</td>
              <td class="amount" style="font-weight: bold;">{{ formatNumber(selectedTransactions.reduce((sum, t) => sum + Number(t.credit_amount), 0)) }}</td>
            </tr>
          </tfoot>
        </table>
        <div class="pagination" style="display: flex; align-items: center; justify-content: center; gap: 1rem;" v-if="totalPages > 1">
          <Buttons caption="Previous" icon="ChevronLeft" @click="prevPage" :disabled="currentPage === 1" />          
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <Buttons caption="Next" icon="ChevronRight" @click="nextPage" :disabled="currentPage === totalPages" />
        </div>
            </div>
      </template>
      </ModalFormTemplate>

  </div>
</template>

<style scoped>
.profit-loss {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.report-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a5fb4;
  text-align: center;
  margin-bottom: 2rem;
}

.filters {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.filter-group label {
  font-size: 0.875rem;
  color: #495057;
  font-weight: 500;
}

.outlet-dropdown {
  width: 100%;
}

.calendar-container {
  width: 100%;
}

.calendar-container :deep(.p-calendar) {
  width: 100%;
}

.calendar-container :deep(.p-datepicker) {
  min-width: 350px;
}

.calendar-container :deep(.calendar-footer) {
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem;
  border-top: 1px solid #dee2e6;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
}

.report-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
}

.section {
  margin-bottom: 2rem;
}

.section-header {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a5fb4;
  border-bottom: 2px solid #1a5fb4;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.line-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: 0.8rem;
  margin-left: 1rem;
  cursor: zoom-in;
}

.item-label {
  color: #495057;
}

.line-item span:last-child {
  font-family: monospace;
}

.subtotal {
  display: flex;
  justify-content: space-between;
  margin-left: 1rem;
  padding: 0.75rem 0;
  font-weight: 600;
  border-top: 1px solid #dee2e6;
  border-bottom: 2px solid #353636;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
}

.major-total {
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  font-weight: 700;
  font-size: 1.1rem;
  color: #1a5fb4;
  border-top: 2px solid #1a5fb4;
  border-bottom: 2px solid #1a5fb4;
  margin: 2rem 0;
  background-color: #f8f9fa;
}

.net-profit {
  color: #2ec27e;
  border-color: #2ec27e;
  font-size: 1.25rem;
}

.transaction-history table {
  width: 100%;
  border-collapse: collapse;
}

.transaction-history th,
.transaction-history td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
}

.transaction-history th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.transaction-history .amount {
  text-align: right;
  font-family: monospace;
}

@media (max-width: 768px) {
  .profit-loss {
    padding: 1rem;
  }

  .filters {
    padding: 1rem;
    gap: 1rem;
  }

  .report-container {
    padding: 1rem;
  }

  .line-item {
    margin-left: 0.5rem;
  }
}
</style>